/* eslint-disable camelcase */
/* eslint-disable react/destructuring-assignment */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslations } from '@npco/utils-translations'
import {
  Anchor,
  ButtonLink,
  COLOR,
  INPUT_SIZE,
  InputWithoutLabel,
  type InputWithoutLabelProps,
  SelectSize,
  SelectStyle,
} from '@npco/zeller-design-system'

import { ReactComponent as ExclamationIcon } from 'assets/svg/exclamation.svg'
import { ReactComponent as MagnifierIcon } from 'assets/svg/search.svg'
import { ListLoader as LoaderSpinner } from 'components/Lists'
import { translationsShared } from 'translations'

import {
  SelectTriggerAdaptive,
  type SelectTriggerAdaptiveProps,
} from '../../Select/SelectTriggerAdaptive'
import type {
  MultiSelectBasicProps,
  MultiSelectItemBasic as Option,
} from '../MultiSelect.types'
import { MultiSelectBasic } from '../MultiSelectBasic/MultiSelectBasic'
import { MultiSelectItem } from '../MultiSelectItem/MultiSelectItem'
import * as styled from './MultiSelectAdaptive.styled'
import { LoaderSkeleton } from './MultiSelectAdaptive/LoaderSkeleton'
import { MultiSelectAdaptiveDeprecated } from './MultiSelectAdaptive/MultiSelectAdaptiveDeprecated'
import { SelectAllOptionValue } from './MultiSelectAdaptive/SelectAllValueOption'
import { ValueLabelDefault } from './MultiSelectAdaptive/ValueLabelDefault'
import type { MultiSelectAdaptiveProps as Props } from './MultiSelectAdaptiveProps'

export type { Props as MultiSelectAdaptiveProps }
export { SelectAllOptionValue as MultiSelectAdaptiveSelectAllOptionValue } from './MultiSelectAdaptive/SelectAllValueOption'
export { PresetSites as MultiSelectAdaptivePresetSites } from './MultiSelectAdaptive/PresetSites'
export { ValueLabelDefault as MultiSelectAdaptiveValueLabelDefault } from './MultiSelectAdaptive/ValueLabelDefault'

const MultiSelectAdaptive = <TOption extends Option>({
  value: valueProp,
  onChangeValue: onChangeValueProp,
  options: optionsProp,
  label: labelProp,
  placeholder: placeholderProp,
  placeholderIcon: placeholderIconProp,
  valueLabel: valueLabelProp,
  selectAll: selectAllProp = true,
  search: searchProp = true,
  emptyMessage: emptyMessageProp,
  emptyActions: emptyActionsProp,
}: Props<TOption>) => {
  // dependencies
  const tShared = useTranslations(translationsShared)

  // state
  const [searchValue, setSearchValue] = useState('')
  const [fetcherState, setFetcherState] = useState<{
    /**
     * `true` if options are being fetched via the `options.fetcher`.
     */
    isFetching: boolean | 'settling'
    options?: TOption[]
    /**
     * `true` if options are being fetched via
     * `options.fetcher.initialOptions.fetcher`.
     */
    isPrefetching: boolean
    initialOptions?: TOption[]
    nextToken?: unknown
    error?: boolean | string
  }>({ isFetching: false, isPrefetching: false })

  // computed
  const value = useMemo(() => {
    if (valueProp) {
      return valueProp
    }
    return []
  }, [valueProp])

  const optionsMap = useMemo(() => {
    const optionsMap = new Map<string, TOption>()

    if (optionsProp) {
      if (Array.isArray(optionsProp)) {
        optionsProp.forEach((option) => optionsMap.set(option.value, option))
      } else {
        fetcherState.initialOptions?.forEach((option) =>
          optionsMap.set(option.value, option)
        )
        fetcherState.options?.forEach((option) =>
          optionsMap.set(option.value, option)
        )
      }
    }
    return optionsMap
  }, [optionsProp, fetcherState])

  const valueLabel = useMemo(() => {
    if (valueLabelProp) {
      return valueLabelProp
    }
    return ValueLabelDefault({
      tShared,
      allKey: 'allSelected',
      manyKey: 'nSelected',
    })
  }, [tShared, valueLabelProp])

  const SelectAllOption = useMemo<TOption>(() => {
    const props_selectAll_label =
      typeof selectAllProp === 'object' ? selectAllProp.label : undefined
    const label = props_selectAll_label ?? tShared('selectAll')
    return { value: SelectAllOptionValue, label } as TOption
  }, [tShared, selectAllProp])

  const callbackState = useMemo(() => {
    return {
      value,
      search: searchValue,
      options: Array.from(optionsMap.values()),
      optionsMap,
    }
  }, [value, searchValue, optionsMap])

  const options_async =
    optionsProp && !Array.isArray(optionsProp) ? optionsProp : undefined

  const initialOptions_async =
    options_async && !Array.isArray(options_async.initialOptions)
      ? options_async.initialOptions
      : undefined

  const isPrefetching =
    initialOptions_async &&
    (fetcherState.isPrefetching || !fetcherState.initialOptions)

  // effects
  useEffect(() => {
    if (!initialOptions_async) {
      return
    }

    if (fetcherState.isPrefetching) {
      return
    }

    if (fetcherState.initialOptions !== undefined) {
      return
    }

    if (value === SelectAllOptionValue || !value.length) {
      setFetcherState((state) => ({
        ...state,
        isPrefetching: false,
        initialOptions: [],
      }))
      return
    }

    ;(async () => {
      setFetcherState((state) => ({ ...state, isPrefetching: true }))
      const result = await initialOptions_async.fetcher(callbackState)
      setFetcherState((state) => ({
        ...state,
        isPrefetching: false,
        initialOptions: result?.options ?? [],
      }))
    })()
  }, [
    value,
    initialOptions_async,
    callbackState,
    fetcherState.isPrefetching,
    fetcherState.initialOptions,
  ])

  // SelectTriggerAdaptive
  const SelectTriggerAdaptive_selectedItem = useMemo<
    SelectTriggerAdaptiveProps['selectedItem']
  >(() => {
    if (isPrefetching) {
      const label =
        initialOptions_async.loadingPlaceholder ?? `${tShared('loading')}…`
      return { label, value: '' }
    }
    if (valueLabel) {
      const label =
        typeof valueLabel === 'function'
          ? valueLabel(callbackState)
          : valueLabel
      return label ? { label, value: '' } : null
    }
    return null
  }, [
    isPrefetching,
    valueLabel,
    initialOptions_async?.loadingPlaceholder,
    tShared,
    callbackState,
  ])

  // MultiSelectBasic
  const MultiSelectBasic_selectedItems: MultiSelectBasicProps<TOption>['selectedItems'] =
    useMemo(() => {
      if (value === SelectAllOptionValue) {
        return [SelectAllOption]
      }
      return value?.map((id) => optionsMap.get(id)!)
    }, [value, optionsMap, SelectAllOption])

  const MultiSelectBasic_onChange = useCallback<
    MultiSelectBasicProps<TOption>['onChange']
  >(
    (selectedItems) => {
      if (onChangeValueProp) {
        if (value === SelectAllOptionValue) {
          return onChangeValueProp([])
        }
        if (selectedItems.includes(SelectAllOption)) {
          return onChangeValueProp(SelectAllOptionValue)
        }
        return onChangeValueProp(selectedItems.map(({ value }) => value))
      }
      return undefined
    },
    [value, SelectAllOption, onChangeValueProp]
  )

  const {
    options: MultiSelectBasic_items,
    isOptionsEmpty,
    isOptionsFilteredEmpty,
  } = useMemo<{
    options: MultiSelectBasicProps<TOption>['items']
    isOptionsEmpty: boolean
    isOptionsFilteredEmpty: boolean
  }>(() => {
    const isOptionsEmpty = !optionsMap.size

    const actions: Option[] = []
    if (selectAllProp && !searchValue && !isOptionsEmpty) {
      actions.push(SelectAllOption)
    }

    let optionsFormatted = Array.from(optionsMap.values())

    if (searchProp) {
      // Default to client-side filtering if options are not dynamically fetched.
      const props_search_filter_default = Array.isArray(optionsProp)
      const props_search_filter =
        typeof searchProp === 'object'
          ? searchProp.filter
          : props_search_filter_default

      if (props_search_filter !== false) {
        const filterSearchValue = searchValue.toLowerCase()
        optionsFormatted = optionsFormatted.filter((option: TOption) =>
          option.label.toLowerCase().includes(filterSearchValue)
        )
      }
    }

    optionsFormatted = optionsFormatted.sort(
      (a, b) =>
        (value?.includes(a.value) ? 0 : 1) - (value?.includes(b.value) ? 0 : 1)
    )

    const isOptionsFilteredEmpty = !optionsFormatted.length

    return {
      options: (actions as TOption[]).concat(optionsFormatted),
      isOptionsEmpty,
      isOptionsFilteredEmpty,
    }
  }, [
    value,
    optionsMap,
    searchValue,
    SelectAllOption,
    optionsProp,
    selectAllProp,
    searchProp,
  ])

  const MultiSelectBasic_renderItem = useCallback<
    MultiSelectBasicProps<TOption>['renderItem']
  >(
    (renderProps) => (
      <MultiSelectItem<TOption>
        {...renderProps}
        {...(renderProps.item.value !== SelectAllOptionValue &&
        value === SelectAllOptionValue
          ? { isChecked: true, isDisabled: true }
          : undefined)}
        sublabel={renderProps.item.sublabel}
      />
    ),
    [value]
  )

  const MultiSelectBasic_renderTrigger = useCallback<
    MultiSelectBasicProps<TOption>['renderTrigger']
  >(
    (renderProps) => (
      <SelectTriggerAdaptive
        {...renderProps}
        label={labelProp}
        placeholder={placeholderProp}
        placeholderIcon={placeholderIconProp}
        selectedItem={SelectTriggerAdaptive_selectedItem}
        isLoading={isPrefetching}
      />
    ),
    [
      SelectTriggerAdaptive_selectedItem,
      isPrefetching,
      labelProp,
      placeholderProp,
      placeholderIconProp,
    ]
  )

  const MultiSelectBasic_renderTopSection_search = useMemo(():
    | {
        placeholder: string
        onChange: NonNullable<InputWithoutLabelProps['onChange']>
      }
    | undefined => {
    if (!(searchProp && !isOptionsEmpty)) {
      return undefined
    }

    return {
      placeholder:
        (typeof searchProp === 'object' ? searchProp.label : undefined) ??
        tShared('search'),
      onChange: (event) => {
        setSearchValue(event.target.value)
      },
    }
  }, [isOptionsEmpty, searchProp, tShared])

  const fetcherErrorMessage = useMemo(() => {
    return (
      (typeof fetcherState.error === 'string'
        ? fetcherState.error
        : undefined) ?? tShared('failedToLoadOptions')
    )
  }, [fetcherState.error, tShared])

  const searchEmptyMessage = useMemo(() => {
    const props_search_emptyMessage =
      typeof searchProp === 'object' ? searchProp.emptyMessage : undefined
    return (
      (typeof props_search_emptyMessage === 'function'
        ? props_search_emptyMessage(callbackState)
        : undefined) ??
      tShared('weCouldntFindAnythingThatMatchesInput', {
        input: searchValue,
      })
    )
  }, [callbackState, searchProp, searchValue, tShared])

  const emptyMessage = useMemo(() => {
    return (
      (typeof emptyMessageProp === 'function'
        ? emptyMessageProp(callbackState)
        : emptyMessageProp) ?? tShared('noAvailableOptions')
    )
  }, [callbackState, emptyMessageProp, tShared])

  const emptyActions = useMemo(() => {
    return typeof emptyActionsProp === 'function'
      ? emptyActionsProp(callbackState)
      : emptyActionsProp
  }, [callbackState, emptyActionsProp])

  const fetcherRetry = useCallback(() => {
    // Retrying clears the error state to retrigger onScrollAtBottom.
    setFetcherState((state) => ({ ...state, error: undefined }))
  }, [])

  const MultiSelectBasic_renderTopSection_prompt = useMemo(():
    | { message: string; actions?: Props.Action[] }
    | undefined => {
    if (options_async && fetcherState.isFetching) {
      return undefined
    }

    if (options_async && fetcherState.error) {
      return {
        message: fetcherErrorMessage,
        actions: [
          {
            label: tShared('retry'),
            onClick: fetcherRetry,
          },
        ],
      }
    }

    if (searchValue && isOptionsFilteredEmpty) {
      return { message: searchEmptyMessage }
    }

    if (isOptionsEmpty) {
      return { message: emptyMessage, actions: emptyActions }
    }

    return undefined
  }, [
    options_async,
    fetcherState.isFetching,
    fetcherState.error,
    searchValue,
    isOptionsFilteredEmpty,
    isOptionsEmpty,
    fetcherErrorMessage,
    tShared,
    fetcherRetry,
    searchEmptyMessage,
    emptyMessage,
    emptyActions,
  ])

  const MultiSelectBasic_renderTopSection = useCallback<
    NonNullable<MultiSelectBasicProps<TOption>['renderTopSection']>
  >(() => {
    if (
      options_async &&
      !fetcherState.options &&
      !fetcherState.error &&
      !optionsMap.size
    ) {
      return <LoaderSkeleton />
    }

    const search = MultiSelectBasic_renderTopSection_search
    const prompt = MultiSelectBasic_renderTopSection_prompt

    return (
      <>
        <div style={{ marginBottom: 8 }}>
          {!!search && (
            <InputWithoutLabel
              name="search"
              value={searchValue}
              onChange={search.onChange}
              icon={MagnifierIcon}
              size={INPUT_SIZE.SMALL}
              placeholder={search.placeholder}
            />
          )}
        </div>
        {!!prompt && (
          <styled.divPrompt>
            <styled.divPromptMessage>
              <div>
                <ExclamationIcon
                  style={{ color: COLOR.YELLOW_1000, marginTop: 2 }}
                />
              </div>
              {prompt.message}
            </styled.divPromptMessage>
            {prompt.actions ? (
              <styled.divPromptActions>
                {prompt.actions.map((action) =>
                  'href' in action ? (
                    <Anchor key={action.label} href={action.href}>
                      {action.label}
                    </Anchor>
                  ) : (
                    <ButtonLink key={action.label} onClick={action.onClick}>
                      {action.label}
                    </ButtonLink>
                  )
                )}
              </styled.divPromptActions>
            ) : undefined}
          </styled.divPrompt>
        )}
      </>
    )
  }, [
    options_async,
    fetcherState.options,
    fetcherState.error,
    optionsMap.size,
    MultiSelectBasic_renderTopSection_search,
    MultiSelectBasic_renderTopSection_prompt,
    searchValue,
  ])

  const MultiSelectBasic_renderBottomSection = useCallback<
    NonNullable<MultiSelectBasicProps<TOption>['renderBottomSection']>
  >(() => {
    return (
      <>
        {!!(options_async && fetcherState.isFetching && optionsMap.size) && (
          <LoaderSpinner />
        )}
      </>
    )
  }, [fetcherState.isFetching, optionsMap.size, options_async])

  const settleTimeout = useRef<ReturnType<typeof setTimeout>>()
  const MultiSelectBasic_onScrollAtBottom = useCallback(async () => {
    if (!options_async) {
      return
    }

    if (fetcherState.isFetching === true) {
      return
    }

    // Stop attempting to fetch if there is an error.
    if (fetcherState.error) {
      return
    }

    // If fetcher has already fetched at least once and has no more pages fetch.
    if (fetcherState.options && !fetcherState.nextToken) {
      return
    }

    clearTimeout(settleTimeout.current)
    setFetcherState((state) => ({ ...state, isFetching: true }))
    const fetched = await options_async.fetcher({
      ...callbackState,
      nextToken: fetcherState.nextToken,
    })

    if (fetched.error) {
      setFetcherState((state) => ({
        ...state,
        isFetching: false,
        error: fetched.error,
        options: fetched.options ?? state?.options,
        nextToken: fetched.nextToken ?? state?.nextToken,
      }))
      return
    }

    setFetcherState((state) => ({
      ...state,
      isFetching: 'settling',
      options: fetched.options ?? state?.options,
      nextToken: fetched.nextToken,
    }))
    settleTimeout.current = setTimeout(() => {
      setFetcherState((state) => ({ ...state, isFetching: false }))
    }, 100)
  }, [
    options_async,
    fetcherState.isFetching,
    fetcherState.error,
    fetcherState.options,
    fetcherState.nextToken,
    callbackState,
  ])

  const MultiSelectBasic_onClose = useCallback(() => {
    setSearchValue('')
  }, [])

  const MultiSelectBasic_mobileLabel = labelProp ?? tShared('selectOptions')

  return (
    <MultiSelectBasic
      items={MultiSelectBasic_items}
      selectedItems={MultiSelectBasic_selectedItems}
      onChange={MultiSelectBasic_onChange}
      renderItem={MultiSelectBasic_renderItem}
      renderTrigger={MultiSelectBasic_renderTrigger}
      renderTopSection={MultiSelectBasic_renderTopSection}
      renderBottomSection={MultiSelectBasic_renderBottomSection}
      selectSize={SelectSize.Medium}
      selectStyle={SelectStyle.Standard}
      onScrollAtBottom={MultiSelectBasic_onScrollAtBottom}
      onClose={MultiSelectBasic_onClose}
      mobileLabel={MultiSelectBasic_mobileLabel}
    />
  )
}

const MultiSelectAdaptiveWrapper = <TOption extends Option>(
  props: Props<TOption> | Props.Deprecated<TOption>
) => {
  if ('items' in props) {
    return <MultiSelectAdaptiveDeprecated {...props} />
  }
  return <MultiSelectAdaptive {...props} />
}

export { MultiSelectAdaptiveWrapper as MultiSelectAdaptive }
